import { useForm } from "react-hook-form";
import { useEnrollPinMutation } from "../../hooks/queries/useAuthQueries";
import { toastService } from "../../providers";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { parsePinCode } from "../../services/utils/pinAuthUtils";
import { pinSetupValidators } from "../../lib/validations/authValidations";
import {
  AUTH_STEP_UP_CODES,
  PIN_ENROLL_CODES,
} from "../../components/auth/config/authActionCodes";
import { usePinChallenge } from "./hooks/usePinChallenge";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import PinChallengeCountdown from "../../components/auth/PinChallengeCountdown/PinChallengeCountdown";
import PinSetupFields from "../../components/auth/PinSetupFields/PinSetupFields";
import CustomButton from "../../components/ui/CustomButton/CustomButton";

const CHALLENGE_DEAD_CODES = new Set([
  AUTH_STEP_UP_CODES.PIN_CHALLENGE_INVALID,
  PIN_ENROLL_CODES.CHALLENGE_ALREADY_USED,
  PIN_ENROLL_CODES.ALREADY_SET,
]);

function SetupPinPage() {
  const { mutate: enrollPin, isPending } = useEnrollPinMutation();
  const { pinChallengeId, secondsLeft, redirectToLogin, completeLogin } =
    usePinChallenge();

  const { control, handleSubmit, reset, trigger, watch } = useForm({
    defaultValues: { newPin: "", confirmPin: "" },
    mode: "onTouched",
  });

  const newPin = watch("newPin");
  const confirmPin = watch("confirmPin");
  const hasFormErrors =
    Boolean(pinSetupValidators.newPin(newPin)) ||
    Boolean(pinSetupValidators.confirmPin(newPin, confirmPin));

  const onSubmit = (values) => {
    enrollPin(
      {
        challengeId: pinChallengeId,
        newPin: values.newPin,
        confirmPin: values.confirmPin,
      },
      {
        onSuccess: (data) => {
          if (data.accessToken && data.refreshToken) {
            completeLogin(data, "PIN set successfully. Login complete.");
            return;
          }

          const code = parsePinCode(null, data);

          if (CHALLENGE_DEAD_CODES.has(code)) {
            toastService.error(
              "Your session has expired. Please log in again.",
            );
            redirectToLogin("pin_challenge_expired");
            return;
          }

          reset({ newPin: "", confirmPin: "" });
          toastService.error(
            data.remarks || "Could not set your PIN. Please try again.",
          );
        },
        onError: (error) => {
          const code = parsePinCode(error, null);

          if (CHALLENGE_DEAD_CODES.has(code)) {
            toastService.error(
              "Your session has expired. Please log in again.",
            );
            redirectToLogin("pin_challenge_expired");
            return;
          }

          reset({ newPin: "", confirmPin: "" });
          toastService.error(
            extractAxiosErrorMessage(
              error,
              "Could not set your PIN. Please try again.",
            ),
          );
        },
      },
    );
  };

  return (
    <AuthLayout>
      <AuthCard
        subtitle={
          <div className="text-black-50 text-xs sm:text-[15px]">
            Set up a PIN to finish logging in
          </div>
        }
      >
        <form
          className="flex flex-col gap-2.5 sm:gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <PinSetupFields
            control={control}
            disabled={isPending}
            trigger={trigger}
          />
          <PinChallengeCountdown secondsLeft={secondsLeft} />
          <CustomButton
            type="submit"
            radius="xl"
            size="md"
            fullWidth
            disabled={isPending || hasFormErrors}
            loading={isPending}
          >
            Set PIN
          </CustomButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default SetupPinPage;
